import { toLibraryPost, toLibraryPostPage, toSharedLibraryPost, type WirePost } from './mappers';
import { expandsCancerTypeSubtree } from '../model/listParams';
import type { LibraryPostsGateway } from '../ports/libraryPostsGateway';

import { seedPostComments } from '@/features/comments/api';
import { fetchClient } from '@/util/fetchClient';
import { queryClient } from '@/util/queryClient';

const HIGHLIGHT_TAGS = { highlightPreTag: '<em>', highlightPostTag: '</em>' } as const;

/**
 * The Inquiry Library's backend, over the generated API client.
 *
 * A post is read with its comments in the same request, since both count
 * against one rate-limited read budget, and those comments seed the thread
 * through the comments feature, which shapes them as a thread read would. A shared post is read from the public
 * endpoint keyed solely on its opaque token: the client omits the Authorization
 * header for anonymous visitors and the endpoint ignores it otherwise, so the
 * result is identical whoever opens the link. A revoked or unknown token throws.
 */
export const fetchLibraryPostsGateway: LibraryPostsGateway = {
  async listPosts({ page = 1, limit = 25, cancerTypeId, includeDescendants, q, saved, sortBy }) {
    const { data } = await fetchClient.GET('/v2/community-posts', {
      params: {
        query: {
          page,
          limit,
          cancerTypeId,
          includeDescendants: expandsCancerTypeSubtree({ cancerTypeId, includeDescendants })
            ? 'true'
            : undefined,
          q,
          saved,
          sortBy,
          attributesToHighlight: ['title', 'body'],
          ...HIGHLIGHT_TAGS,
          attributesToSnippet: ['body:30'],
          snippetEllipsisText: '…',
        },
      },
    });

    if (!data?.data) {
      throw new Error('No data returned from API');
    }

    return toLibraryPostPage(data.data);
  },

  async getPost({ postId, q }, { userId }) {
    const { data } = await fetchClient.GET('/v2/community-posts/{postId}', {
      params: {
        path: { postId },
        query: q
          ? { include: 'comments', q, attributesToHighlight: ['title', 'body'], ...HIGHLIGHT_TAGS }
          : { include: 'comments' },
      },
    });

    if (data?.comments) {
      await seedPostComments(queryClient, userId, postId, data.comments);
    }

    return data?.data ? toLibraryPost(data.data as WirePost) : undefined;
  },

  async getSharedPost(token) {
    const { data } = await fetchClient.GET('/v2/public/community-posts/{token}', {
      params: { path: { token } },
    });

    if (!data?.data) {
      throw new Error('Shared community post not found');
    }

    return toSharedLibraryPost(data.data);
  },

  async setLiked(postId, liked) {
    if (liked) {
      await fetchClient.POST('/v2/community-posts/{postId}/likes', {
        params: { path: { postId } },
      });

      return;
    }

    await fetchClient.DELETE('/v2/community-posts/{postId}/likes', {
      params: { path: { postId } },
    });
  },

  async setSaved(postId, saved) {
    if (saved) {
      await fetchClient.POST('/v2/community-posts/{postId}/saves', {
        params: { path: { postId } },
      });

      return;
    }

    await fetchClient.DELETE('/v2/community-posts/{postId}/saves', {
      params: { path: { postId } },
    });
  },
};
